import React, { createContext, useContext, useState } from 'react';
import usePage from '@dashboard/hooks/usePage';
import { useFields } from './FieldsContext';
import { useQueryClient } from '@tanstack/react-query';
import request from '@dashboard/services/request';
import { apiUrl } from '@dashboard/services/env';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { pageKey } from '@dashboard/lib/queryKeys';
import { confirm } from '@dashboard/lib/confirm';
import { uniqueId } from 'lodash';
import { elementFieldTypeEnums } from '@dashboard/services/pageFieldTypes';

const FieldEditContext = createContext();

const reorderFields = async (sellerId, pageId, data, token) =>
  request(`${apiUrl}/sellers/${sellerId}/pages/${pageId}/fields/reorder`, token, {
    method: 'POST',
    body: JSON.stringify(data),
  });

export function FieldEditProvider({ children }) {
  const { page } = usePage();
  const { token, sellerId } = useAuth();
  const queryClient = useQueryClient();

  const { state: fields, dispatch } = useFields();

  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isAddingField, setIsAddingField] = useState(false);
  const [isReordering, setIsReordering] = useState(false);

  async function onReorderFields(reorderedFields) {
    setIsReordering(true);
    const originalFields = [...fields];

    // Stamp each field with its new order index. The checkout preview sorts
    // fields by `field.order` (CheckoutForm sorts by order, not array index),
    // so the optimistic cache write must update each field's order property
    // for the preview to reflect the new sequence before the server refetch.
    const reorderedWithOrder = reorderedFields.map((f, i) => ({ ...f, order: i }));

    dispatch({ type: 'set', values: reorderedWithOrder });

    const fieldIds = reorderedWithOrder.map(f => f._id);

    const response = await reorderFields(sellerId, page._id, { order: fieldIds }, token);

    if (!response.data) {
      dispatch({ type: 'set', values: originalFields });
      queryClient.invalidateQueries({ queryKey: pageKey(page._id) });
    } else {
      queryClient.setQueryData(pageKey(page._id), oldData => {
        if (!oldData) return oldData;
        return { ...oldData, fields: reorderedWithOrder };
      });
    }
    setIsReordering(false);
  }

  async function setIsEditingField(isEditing, fieldId) {
    if (isAddingField) return;

    let proceed = true;
    if (hasUnsavedChanges) {
      proceed = await confirm('Discard unsaved changes?', {
        confirm: 'Discard',
      });
    }

    if (!proceed) return;

    setHasUnsavedChanges(false);

    dispatch({
      type: 'set',
      values: fields.map(f => ({
        ...f,
        isEditing: f._id === fieldId ? isEditing : false,
      })),
    });
  }

  function onAddField(element = elementFieldTypeEnums.TEXT, preset = {}) {
    const id = uniqueId();

    dispatch({
      type: 'add',
      values: [
        {
          ...preset,
          element,
          key: id,
          showHideLogic: {
            enabled: false,
            comparison: 'IS',
            value: '',
            element: {
              elementType: 'field',
              elementId: '',
              elementTitle: '',
            },
          },
          isEditing: true,
          isNew: true,
        },
      ],
    });

    setIsAddingField(true);
    return id;
  }

  return (
    <FieldEditContext.Provider
      value={{
        onAddField,
        isAddingField,
        setIsAddingField,
        onReorderFields,
        hasUnsavedChanges,
        setHasUnsavedChanges,
        setIsEditingField,
        isReordering,
      }}
    >
      {children}
    </FieldEditContext.Provider>
  );
}

export const useFieldEdit = () => useContext(FieldEditContext);
